import { handleReturnedResponse } from "@/models/equip-api-response-objects/handle-returned-response";
import { GetDateTimeFormatted } from "./utilities-time";
import { equipOptionsInstance } from "../models/EquipOptions";
import { retrieveHydrator, validKeyType } from "./object-types-hydrators-map";

export type CachedValue = string | number | undefined;
export type ReqResult = { requestType: string; result: CachedValue };
export function formatCachedValue(value: CachedValue): string | undefined {
  if (value === undefined) {
    return undefined;
  }
  // if (value instanceof Date) {
  //   return value.toLocaleDateString();
  // }
  return String(value);
}

export class EquipDataCache {
  private trackedKeys: Map<string, boolean> = new Map<string, boolean>();
  private cache: Map<string, ReqResult> = new Map<string, ReqResult>();
  private subscribers: (() => void)[] = [];
  private cacheTimer: ReturnType<typeof setTimeout> | undefined;
  public LastRefreshed: string = "never";

  set(key: validKeyType, requestType: string, value: CachedValue) {
    this.cache.set(this.makeStringKey(key), { requestType: requestType, result: value });
    this.subscribers.forEach((x) => x());
  }

  get(key: validKeyType): CachedValue {
    return this.cache.get(this.makeStringKey(key))?.result ?? null;
  }

  haskey(key: validKeyType): boolean {
    return this.cache.has(this.makeStringKey(key));
  }

  makeStringKey(o: validKeyType): string {
    const keys = Object.keys(o).sort();
    return JSON.stringify(o, keys);
  }

  makeRequestFromStringKey(o: string): validKeyType {
    return JSON.parse(o);
  }

  public async RehydrateCache(): Promise<void> {
    this.LastRefreshed = GetDateTimeFormatted();
    const cacheKeys = [...this.cache.keys()];
    await Promise.all(
      cacheKeys.map(async (key) => {
        const val = this.cache.get(key);
        try {
          const originalRequest = this.makeRequestFromStringKey(key);
          const resp = await retrieveHydrator(val.requestType)(originalRequest);
          const validResp = handleReturnedResponse(resp);
          this.cache.set(key, { requestType: val.requestType, result: validResp });
        } catch (error) {
          console.error(error);
          this.cache.set(key, { requestType: val.requestType, result: "#ERROR" });
        }
      })
    );

    this.cacheTimer = setTimeout(() => {
      console.log(`CACHE EXPIRED`);
      this.ExpireCache();
    }, 60_000);

    this.subscribers.forEach((x) => x());
  }

  public ExpireCache() {
    this.cacheTimer = undefined;
    const keysToDelete = [];
    this.cache.forEach((_, key) => !this.trackedKeys.has(key) && keysToDelete.push(key));
    keysToDelete.forEach((key) => this.cache.delete(key));
    this.cache.forEach((val, key) =>
      this.cache.set(key, { requestType: val.requestType, result: "#WAITING" })
    );
    this.subscribers.forEach((x) => x());
  }
  public ExpireRequestMap(): void {
    this.trackedKeys = new Map();
  }

  public StopCache(): void {
    this.ExpireRequestMap();
    this.ExpireCache();
    this.LastRefreshed = "never";
    this.subscribers.forEach((x) => x());
  }

  public Expose(): { map: Map<validKeyType, ReqResult>; lastRefreshed: string } {
    const newMap = new Map<validKeyType, ReqResult>();
    for (const [k, v] of this.cache) {
      const obj = this.makeRequestFromStringKey(k);
      newMap.set(obj, v);
    }
    return { map: newMap, lastRefreshed: this.LastRefreshed };
  }

  public async Get(request: validKeyType, requestType: string): Promise<CachedValue> {
    if (!equipOptionsInstance.Online) return "#OFFLINE";

    switch (equipOptionsInstance.CalculationMode) {
      case "BATCH": {
        const key = request;
        this.trackedKeys.set(this.makeStringKey(key), true);

        if (!this.haskey(key)) {
          console.log("no key!!!");
          this.set(key, requestType, "#WAITING");
        }

        return this.get(key);
      }
      default:
      case "DIRECT": {
        var resp = await retrieveHydrator(requestType)(request);
        return handleReturnedResponse(resp);
      }
    }
  }

  public Subscribe(callback: () => void) {
    this.subscribers.push(callback);
  }
}

export const equipDataCacheInstance = new EquipDataCache();
